import {
  AnyDynamicFormComponentRegistry,
  DynamicFormFieldConfig,
  QuestionnaireComponentKind,
} from '@main/dynamic-form';
import { VendorQuestionnaireFormAnswerValue } from '@main/graphql/client-scalars';
import { Vendor_Questionnaire_Form_Answer_Statuses_Enum } from '@main/graphql/types.generated';
import { createSorter } from '@main/shared/utils';

import {
  QuestionnaireFormFieldConfig,
  QuestionnnaireFormFieldAnswer,
  QuestionnnaireFormFilterMode,
  QuestionnnaireFormFilters,
} from './form-types';

export const QUESTION_STATUSES = {
  [Vendor_Questionnaire_Form_Answer_Statuses_Enum.Pending]: {
    value: 'questionnaires.form.questionStatus.pending',
    tone: 'neutral',
  },
  [Vendor_Questionnaire_Form_Answer_Statuses_Enum.Completed]: {
    value: 'questionnaires.form.questionStatus.completed',
    tone: 'success',
  },
} as const;

export function getQuestionFilter(filters: QuestionnnaireFormFilters) {
  return <TRegistry extends AnyDynamicFormComponentRegistry>(
    config: DynamicFormFieldConfig<TRegistry>,
    answer?: QuestionnnaireFormFieldAnswer,
  ) => {
    return (
      (filters.mode === QuestionnnaireFormFilterMode.Unanswered &&
        answer?.status === Vendor_Questionnaire_Form_Answer_Statuses_Enum.Completed) ||
      (filters.search.length > 0 &&
        !!config.label &&
        !config.label.toLowerCase().includes(filters.search))
    );
  };
}

export function createNewAnswerFor(
  field: DynamicFormFieldConfig<AnyDynamicFormComponentRegistry>,
): QuestionnnaireFormFieldAnswer {
  return {
    field_name: field.name,
    status: Vendor_Questionnaire_Form_Answer_Statuses_Enum.Pending,
  };
}

export function reorderFormAnswers<
  TAnswer extends QuestionnnaireFormFieldAnswer,
  TRegisrty extends AnyDynamicFormComponentRegistry,
>(answers: TAnswer[], config: readonly DynamicFormFieldConfig<TRegisrty>[]) {
  const indexMap = Object.fromEntries(config.map((field, index) => [field.name, index]));

  return answers.toSorted((a, b) => (indexMap[a.field_name] ?? 0) - (indexMap[b.field_name] ?? 0));
}

export function isAnswerFile(answer?: VendorQuestionnaireFormAnswerValue | null) {
  return answer != null && typeof answer === 'object' && 'file' in answer;
}

export function createQuestionnaireSorter(direction?: 'asc' | 'desc') {
  const sorter = createSorter(direction);

  return function questionnaireSorter(
    fields?: readonly QuestionnaireFormFieldConfig[] | null,
  ): QuestionnaireFormFieldConfig[] | undefined {
    return fields
      ?.toSorted(sorter)
      .map((f) =>
        f.kind === QuestionnaireComponentKind.Section
          ? { ...f, fields: questionnaireSorter(f.fields) }
          : f,
      );
  };
}

export function findFieldSectionNo(
  fieldName: string,
  config: readonly QuestionnaireFormFieldConfig[],
) {
  let sectionNo = 0;
  let questionNo = 0;
  let questionFound = false;

  for (const field of config) {
    if (field.kind === QuestionnaireComponentKind.Section) {
      sectionNo++;
    } else {
      questionNo++;
    }

    if (field.name === fieldName) {
      questionFound = true;
      break;
    }
  }

  if (questionFound) {
    return { sectionNo, questionNo };
  }

  return;
}
